import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    MenuItem,
    Select,
    CircularProgress,
    Avatar,
    Container
} from '@mui/material';
import { ArrowBackIosNewOutlined, CalendarMonthOutlined, RefreshRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { getTargetMatrixApi } from 'utils/API/Mis Apis/HrPortelApis';
import { useMemo } from 'react';

// ─── Constants ───────────────────────────────────────────────────
const FINANCIAL_YEARS = Array.from({ length: 14 }, (_, i) => {
    const startYear = 2026 + i;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
});

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

const ROLES = [
    { value: 'manager', label: 'Frontline' },
    { value: 'middle_manager', label: 'Team Leader' },
    { value: 'state_head', label: 'State Head' },
    { value: 'zone_head', label: 'Zone Head' },
    { value: 'national_head', label: 'National Head' }
];

const ALL_METRIC_CONFIGS = {
    GTV: { label: 'GTV (₹)', color: '#6366f1' },
    BC: { label: 'BC', color: '#14b8a6' },
    Dist: { label: 'Dist.', color: '#f59e0b' },
    MATM: { label: 'MATM', color: '#ec4899' },
    Aggregator: { label: 'Aggr.', color: '#8b5cf6' }
};

const HIERARCHY_ORDER = [
    { id: 'NH', value: 'national_head' },
    { id: 'ZH', value: 'zone_head' },
    { id: 'SH', value: 'state_head' },
    { id: 'TL', value: 'middle_manager' }, // Team Leader
    { id: 'FL', value: 'manager' } // Frontline
];

const TargetMatrix = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [matrixData, setMatrixData] = useState([]);
    const [financialYear, setFinancialYear] = useState(FINANCIAL_YEARS[0]);
    const [selectedRole, setSelectedRole] = useState(ROLES[0].value);

    const visibleHierarchy = useMemo(() => {
        const selectedIdx = HIERARCHY_ORDER.findIndex((r) => r.value === selectedRole);
        if (selectedIdx === -1) return HIERARCHY_ORDER;
        return HIERARCHY_ORDER.slice(0, selectedIdx + 1);
    }, [selectedRole]);

    const dynamicMetrics = useMemo(() => {
        const uniqueKeys = new Set();
        matrixData.forEach((row) => {
            if (row.months) {
                Object.values(row.months).forEach((monData) => {
                    Object.keys(monData).forEach((key) => uniqueKeys.add(key));
                });
            }
        });
        return Array.from(uniqueKeys).map((key) => ({
            id: key,
            label: ALL_METRIC_CONFIGS[key]?.label || key,
            color: ALL_METRIC_CONFIGS[key]?.color || '#64748b'
        }));
    }, [matrixData]);

    const fetchData = useCallback(async (year, role) => {
        setLoading(true);
        try {
            const response = await getTargetMatrixApi({ financial_year: year, role: role });
            if (response?.data?.success) {
                setMatrixData(response.data.data);
            }
        } catch (error) {
            console.error('Fetch matrix error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(financialYear, selectedRole);
    }, [fetchData, financialYear, selectedRole]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Vibrant Enterprise Blue
                    px: { xs: 2, md: 4 },
                    pt: 3,
                    pb: 8,
                    color: '#fff',
                    borderRadius: '10px'
                }}
            >
                <Container maxWidth="xl">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <IconButton
                            onClick={() => navigate('/HR/target-settings')}
                            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBackIosNewOutlined />
                        </IconButton>
                        <Box>
                            <Typography variant="h3" fontWeight={900} color="#fff" letterSpacing="-0.02em">
                                Target Matrix
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7, color: '#fff' }}>
                                Comparative monthly breakdown of organizational performance benchmarks
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            flexWrap: 'wrap'
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CalendarMonthOutlined sx={{ color: '#3b82f6' }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#fff">
                                Role:
                            </Typography>
                            <Select
                                size="small"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                sx={{
                                    color: '#fff',
                                    minWidth: 150,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                                    '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                }}
                            >
                                {ROLES.map((role) => (
                                    <MenuItem key={role.value} value={role.value}>
                                        {role.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CalendarMonthOutlined sx={{ color: '#3b82f6' }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#fff">
                                Period:
                            </Typography>
                            <Select
                                size="small"
                                value={financialYear}
                                onChange={(e) => setFinancialYear(e.target.value)}
                                sx={{
                                    color: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                                    '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                }}
                            >
                                {FINANCIAL_YEARS.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        <IconButton onClick={() => fetchData(financialYear, selectedRole)} sx={{ color: '#fff' }}>
                            <RefreshRounded />
                        </IconButton>
                    </Paper>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ mt: -4 }}>
                <AnimatePresence mode="wait">
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={60} thickness={4} sx={{ color: '#1e293b' }} />
                        </Box>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0 20px 50px -12px rgba(0,0,0,0.08)'
                                }}
                            >
                                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                                    <Table stickyHeader aria-label="target matrix table">
                                        <TableHead>
                                            <TableRow>
                                                {/* Hierarchy Headers */}
                                                {visibleHierarchy.map((role) => (
                                                    <TableCell
                                                        key={role.id}
                                                        sx={{
                                                            bgcolor: '#f1f5f9',
                                                            fontWeight: 900,
                                                            fontSize: '0.85rem',
                                                            color: '#0f172a',
                                                            zIndex: 11
                                                        }}
                                                    >
                                                        {role.id}
                                                    </TableCell>
                                                ))}

                                                {/* Monthly Metric Headers */}
                                                {MONTHS.map((month) => (
                                                    <TableCell
                                                        key={month}
                                                        align="center"
                                                        colSpan={dynamicMetrics.length}
                                                        sx={{
                                                            bgcolor: '#1e3a8a', // Corporate Blue
                                                            color: '#fff',
                                                            fontWeight: 900,
                                                            fontSize: '0.9rem',
                                                            borderLeft: '1px solid rgba(255,255,255,0.2)',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em'
                                                        }}
                                                    >
                                                        {month}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            <TableRow>
                                                {/* Empty cells for hierarchy padding */}
                                                <TableCell colSpan={visibleHierarchy.length} sx={{ bgcolor: '#f8fafc', p: 0 }} />

                                                {/* Metric labels for each month */}
                                                {MONTHS.map((month) => (
                                                    <METRIC_HEADER_ROW key={month} metrics={dynamicMetrics} />
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matrixData.map((row, idx) => (
                                                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                                                    <HIERARCHY_CELLS row={row} visibleHierarchy={visibleHierarchy} />
                                                    {MONTHS.map((month) => (
                                                        <MONTH_DATA_CELLS
                                                            key={month}
                                                            data={row.months?.[month] || row['0']?.[month]}
                                                            metrics={dynamicMetrics}
                                                        />
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Container>
        </Box>
    );
};

const METRIC_HEADER_ROW = ({ metrics }) => (
    <>
        {metrics.map((metric) => (
            <TableCell
                key={metric.id}
                align="center"
                sx={{
                    fontSize: '0.75rem',
                    fontWeight: 900,
                    color: '#0f172a', // Higher contrast dark blue/slate
                    bgcolor: '#f1f5f9', // Slightly darker background for the sub-header
                    minWidth: 90,
                    px: 1,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                }}
            >
                {metric.label}
            </TableCell>
        ))}
    </>
);

METRIC_HEADER_ROW.propTypes = {
    metrics: PropTypes.array.isRequired
};

const HIERARCHY_CELLS = ({ row, visibleHierarchy }) => (
    <>
        {visibleHierarchy.map((role) => (
            <TableCell key={role.id} sx={{ borderRight: '1px solid #e2e8f0', minWidth: 150 }}>
                {row[role.id] && row[role.id] !== 'N/A' ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.50', color: 'primary.main' }}>
                            {row[role.id].charAt(0)}
                        </Avatar>
                        <Typography variant="caption" fontWeight={700} noWrap>
                            {row[role.id]}
                        </Typography>
                    </Stack>
                ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        -
                    </Typography>
                )}
            </TableCell>
        ))}
    </>
);

HIERARCHY_CELLS.propTypes = {
    row: PropTypes.object.isRequired,
    visibleHierarchy: PropTypes.array.isRequired
};

const MONTH_DATA_CELLS = ({ data, metrics }) => {
    if (!data) return <TableCell colSpan={metrics.length} sx={{ borderLeft: '1px solid #e2e8f0' }} />;
    return (
        <>
            {metrics.map((metric, idx) => (
                <TableCell
                    key={metric.id}
                    align="center"
                    sx={{
                        borderLeft: idx === 0 ? '1px solid #e2e8f0' : 'none',
                        px: 1
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={800}
                        sx={{
                            color: parseFloat(data[metric.id]) > 0 ? 'text.primary' : 'text.disabled',
                            display: 'block'
                        }}
                    >
                        {metric.id === 'GTV' ? `₹${Math.round(parseFloat(data[metric.id])).toLocaleString()}` : data[metric.id]}
                    </Typography>
                </TableCell>
            ))}
        </>
    );
};

MONTH_DATA_CELLS.propTypes = {
    data: PropTypes.object,
    metrics: PropTypes.array.isRequired
};

export default TargetMatrix;
